import React, { useCallback, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';

import { useAnalyticsPrefix, useAuth } from '../../AuthContext';
import { ENTRY_STEP, useAccountForm } from '../AccountFormContext';
import { MethodBadgeAnchor } from './MethodBadgeAnchor';
import { AuthFormError } from './primitives/AuthFormError';
import { CheckEmailNotice, type CheckEmailNoticeMount } from './primitives/CheckEmailNotice';
import { OrDivider } from './primitives/OrDivider';
import { SocialAuthModule } from './SocialAuthModule';

/**
 * Which magic-link surface to render:
 * - `sign-in` / `set-up` — the "check your email" confirmation (differing only
 *   in body copy); the link is (or has just been) sent.
 * - `expired` — the on-page recovery screen shown when a login link has died.
 */
export type MagicLinkVariant = 'sign-in' | 'set-up' | 'expired';

/**
 * Method a last-used badge can anchor to on this module's screens.
 *
 * @public
 */
export type MagicLinkMethod = 'magic-link' | 'google' | 'apple';

export interface MagicLinkModuleProps {
  /**
   * Which surface to render. Read once on mount: it seeds the view and the
   * initial send/cooldown, and changing it afterward will not re-run either.
   * A consumer that needs to switch surfaces at runtime should remount with a
   * `key`. @default 'sign-in'
   */
  variant?: MagicLinkVariant;
  /**
   * Per-method "last used" badge slot: anchored to the resend button for
   * `magic-link`, and forwarded to the social stack for `google`/`apple` on the
   * expired screen. GROWTH-1617 owns the pill and the last-used lookup; this
   * module only anchors whatever it returns.
   */
  renderLastUsedBadge?: (method: MagicLinkMethod) => React.ReactNode;
  /**
   * Sends the link, replacing the default `requestPasswordReset(email)` send.
   * Same injectable shape as `RegistrationModule`'s `register`, and the same
   * `Promise<boolean>` contract `CheckEmailNotice` already takes: resolve
   * `true` only when an email actually went out, since that is what starts the
   * cooldown and licenses the "we sent it" copy. A `false` or a throw renders
   * the transient send-error copy. For a surface whose only handle on the
   * account is an opaque token, where there is no address to reset from.
   */
  onResend?: () => Promise<boolean>;
  /**
   * Where "Enter Password" goes, as an href instead of the default `password`
   * step. Required on a surface with no email: `PasswordModule` signs in with
   * the shared `email`, so the step transition has nothing to submit and the
   * button has to hand off to a form that collects one.
   */
  passwordHref?: string;
}

const ANALYTICS_COMPONENT = 'magic-link';

const CHECK_EMAIL_HEADING = 'Check your email';
const SIGN_IN_BODY = 'It includes a link that will sign you in.';
const SET_UP_BODY = 'It includes a link to set up your account.';
const EXPIRED_HEADING = 'Your login link has expired';

/**
 * Body copy for the expired screen when no email is known, standing in for the
 * "Signing in as {email}" / "Not your account?" pair the Figma draws — both of
 * which name an address this surface does not have.
 */
const EXPIRED_NO_EMAIL_BODY = 'Send a new login link, or sign in another way.';

/**
 * Shown when a send fails. The only reachable failures on the (un-rate-limited)
 * reset-password endpoint are network errors and 5xx, so the copy is a
 * transient "try again", not a rate-limit message.
 */
const SEND_ERROR_MESSAGE = 'Something went wrong sending the email. Please try again.';

/**
 * Shown when the status lookup finds no account for the email — nothing was
 * sent, so the confirmation must not claim otherwise.
 */
const NO_ACCOUNT_MESSAGE = 'We couldn’t find an account for that email.';

/**
 * The V2 magic-link surfaces: the "check your email" confirmation (the shared
 * `CheckEmailNotice`, which owns the resend button and the cooldown) and the
 * on-page expired-link recovery screen (resend, social alternatives, a password
 * handoff, and a switch-account escape back to email entry).
 *
 * Every send rides the session write-path via `useAuth().requestPasswordReset`
 * — the human-status-guarded reset/setup-link flow that a generated-password
 * account uses to get back in — rather than the legacy passwordless mutation
 * that bypassed the session. That call resolving `success` does NOT by itself
 * mean an email went out (an `isNewHuman` lookup sends nothing), so the sent
 * confirmation copy is withheld until a send actually succeeds. Usable
 * standalone under a bare `AccountFormProvider` or as the `magic-link` step
 * after `EmailModule`.
 *
 * Both of those rest on knowing the account's address, and a surface reached
 * from a dead link in an inbox does not: it holds only the link's own token.
 * So the screens degrade when the shared `email` is empty — the "Signing in as"
 * echo and the switch-account escape drop out — and `onResend`/`passwordHref`
 * let that surface substitute a token-derived send and a real handoff for the
 * two behaviours that need an address.
 */
export const MagicLinkModule: React.FC<MagicLinkModuleProps> = ({
  variant = 'sign-in',
  renderLastUsedBadge,
  onResend,
  passwordHref,
}) => {
  const { email, setEmail, setStep, formError } = useAccountForm();
  const { requestPasswordReset } = useAuth();
  const analyticsPrefix = useAnalyticsPrefix();

  const [view, setView] = useState<'check-email' | 'expired'>(
    variant === 'expired' ? 'expired' : 'check-email',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // set-up arrives already sent (registration mailed the setup link); every
  // other path only becomes "sent" once a send succeeds.
  const [hasSent, setHasSent] = useState(variant === 'set-up');
  const [expiredResending, setExpiredResending] = useState(false);

  const analyticsName = (action: string): string =>
    `${analyticsPrefix}-${ANALYTICS_COMPONENT}-${action}`;

  // A surface reached from inside the form flow carries the address the entry
  // step captured; one reached from a dead link in an inbox has only the link's
  // token, so every screen here has to read without an email too.
  const hasEmail = email !== '';

  /**
   * Sends (or resends) the link through the session write-path, owning the
   * error copy. Returns whether an email actually went out — a `success` whose
   * lookup found no account (`isNewHuman`) sent nothing, so it resolves `false`
   * and the notice/expired screen won't claim a send or start a cooldown. An
   * injected `onResend` replaces the transport but keeps that contract; it
   * reports only whether a send happened, so its failures get the transient
   * copy rather than the no-account copy, which its `false` cannot distinguish.
   */
  const sendLink = useCallback(async (): Promise<boolean> => {
    setErrorMessage(null);
    try {
      if (onResend) {
        const sent = await onResend();
        if (sent) {
          setHasSent(true);
        } else {
          setErrorMessage(SEND_ERROR_MESSAGE);
        }
        return sent;
      }
      const result = await requestPasswordReset(email);
      if (result.success && !result.data.isNewHuman) {
        setHasSent(true);
        return true;
      }
      setErrorMessage(result.success ? NO_ACCOUNT_MESSAGE : SEND_ERROR_MESSAGE);
      return false;
    } catch {
      setErrorMessage(SEND_ERROR_MESSAGE);
      return false;
    }
  }, [onResend, requestPasswordReset, email]);

  const handleExpiredResend = useCallback(async () => {
    if (expiredResending) {
      return;
    }
    setExpiredResending(true);
    try {
      const succeeded = await sendLink();
      if (succeeded) {
        setView('check-email');
      }
    } finally {
      setExpiredResending(false);
    }
  }, [expiredResending, sendLink]);

  const handleExpiredResendClick = useCallback(() => {
    void handleExpiredResend();
  }, [handleExpiredResend]);

  const handleSwitchAccount = useCallback(() => {
    setEmail('');
    setStep(ENTRY_STEP);
  }, [setEmail, setStep]);

  const handleEnterPassword = useCallback(() => {
    setStep('password');
  }, [setStep]);

  if (view === 'expired') {
    const passwordButtonProps = {
      className: 'w-full',
      variant: 'secondary',
      'aria-label': 'Enter Password',
      analyticsName: analyticsName('enter-password'),
      children: 'Enter Password',
    } as const;
    return (
      <div className="flex w-full flex-col gap-8">
        <div className="flex w-full flex-col gap-2">
          <Text as="h1" className="text-subheader-md text-od-content-primary">
            {EXPIRED_HEADING}
          </Text>
          {hasEmail ? (
            <>
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Signing in as {email}
              </Text>
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Not your account?{' '}
                <button
                  type="button"
                  onClick={handleSwitchAccount}
                  className="text-od-content-accent-brand hover:underline"
                >
                  Sign into another account
                </button>
              </Text>
            </>
          ) : (
            <Text as="p" className="text-body-sm text-od-content-secondary">
              {EXPIRED_NO_EMAIL_BODY}
            </Text>
          )}
        </div>
        {/* This screen hosts the social stack, whose redirect failures publish
            to the shared `formError` and are suppressed inline — so it owes
            them a region, or a returning `?auth_error=` renders nowhere. A send
            failure is the one the user just triggered, so it takes precedence. */}
        <AuthFormError serverErrors={errorMessage ?? formError} />
        <MethodBadgeAnchor badge={renderLastUsedBadge?.('magic-link')}>
          <Button
            className="w-full"
            variant="primary"
            aria-label="Resend login link"
            analyticsName={analyticsName('resend-login-link')}
            loading={expiredResending}
            disabled={expiredResending}
            onClick={handleExpiredResendClick}
          >
            Resend login link
          </Button>
        </MethodBadgeAnchor>
        <OrDivider />
        <div className="flex w-full flex-col gap-4">
          <SocialAuthModule renderLastUsedBadge={renderLastUsedBadge} />
          {passwordHref != null ? (
            <Button {...passwordButtonProps} href={passwordHref} />
          ) : (
            <Button {...passwordButtonProps} onClick={handleEnterPassword} />
          )}
        </div>
      </div>
    );
  }

  const body = variant === 'set-up' ? SET_UP_BODY : SIGN_IN_BODY;
  // A sign-in confirmation hasn't sent yet (EmailModule only looked the human
  // up), so the notice fires the first send on mount. Every other path here —
  // a set-up link, or an expired-screen resend that just succeeded — already
  // sent, so the notice only starts the cooldown.
  const checkEmailMount: CheckEmailNoticeMount = variant === 'sign-in' ? 'send' : 'cooldown';

  const sentTo = hasEmail ? `We sent an email to ${email}.` : 'We sent you a new email.';

  return (
    <CheckEmailNotice
      heading={CHECK_EMAIL_HEADING}
      description={hasSent ? `${sentTo} ${body}` : undefined}
      error={errorMessage}
      onResend={sendLink}
      onMount={checkEmailMount}
      analyticsName={analyticsName('resend-link')}
    />
  );
};
